<template>
	<!-- Supplier Profile -->
	<modal class="verification-modal" @close="close" :size='"medium"'>
		<template slot="header">
			<h3><b>{{ $t(title) }}</b></h3>
		</template>
		<template slot="body">
			<div v-if="isShowStats">
				<div class="row" v-if="!isLoading">
					<div class="col-md-4">
						<overview
							:text="'Total Products'"
							:type="'normal'"
							:with-additional="false"
							:number="totalProducts">
						</overview>
					</div>
					<div class="col-md-4">
						<overview
							:text="'Total Orders'"
							:type="'normal'"
							:with-additional="false"
							:number="submissionCount">
						</overview>
					</div>
					<div class="col-md-4">
						<overview
							:text="'Total Balance'"
							:type="'normal'"
							:with-additional="false"
							:number="balance">
						</overview>
					</div>
				</div>
				<div v-else class="is-loading mb-4"></div>
			</div>
			<div v-if="user">
				<div class="row">
					<div class="col-md-6 mb-2">
						<div class="text-bold">Name</div>
						<div>{{ userName }}</div>
					</div>
					<div class="col-md-6 mb-2">
						<div class="text-bold">Phone</div>
						<div>{{ userPhone }}</div>
					</div>
					<div class="col-md-6 mb-2">
						<div class="text-bold">Email</div>
						<div>{{ userEmail }}</div>
					</div>
				</div>
				<hr />
				<div class="row">
					<div class="col-md-6 mb-2">
						<div class="text-bold">Business Name</div>
						<div>{{ userBusinessName }}</div>
					</div>
					<div class="col-md-6 mb-2">
						<div class="text-bold">Business Address</div>
						<div>{{ userBusinessAddress }}</div>
					</div>
					<div class="col-md-12 mb-2">
						<div class="text-bold">Business City</div>
						<div>{{ userBusinessCity }}</div>
					</div>
				</div>
				<hr v-if="isShowPicture || isShowLocation" />
				<div class="row" v-if="isShowPicture || isShowLocation">
					<div class="col-md-6 mb-2" v-if="isShowPicture">
						<div class="text-bold">Warehouse Photo</div>
						<div class="store-photo">
							<img :src="warehousePhoto" class="store-logo-image" v-if="warehousePhoto !== ''">
							<div class="store-logo-upload-button d-flex align-items-center justify-content-center" v-if="warehousePhoto === ''">
								<i class="icon material-icons camera-icon">camera_alt</i>
							</div>
						</div>
					</div>
					<div class="col-md-6 mb-2" v-if="isShowPicture">
						<div class="text-bold">Warehouse Photo With Owner</div>
						<div class="store-photo">
							<img :src="warehousePhotoWithOwner" class="store-logo-image" v-if="warehousePhotoWithOwner !== ''">
							<div class="store-logo-upload-button d-flex align-items-center justify-content-center" v-if="warehousePhotoWithOwner === ''">
								<i class="icon material-icons camera-icon">camera_alt</i>
							</div>
						</div>
					</div>
					<div class="col-md-6 mb-2" v-if="isShowLocation">
						<div class="text-bold">Maps Latitude</div>
						<div>{{ latitude }}</div>
					</div>
					<div class="col-md-6 mb-2" v-if="isShowLocation">
						<div class="text-bold">Maps Longitude</div>
						<div>{{ longitude }}</div>
					</div>
					<div class="col-md-12 mb-2" v-if="isMapEnabled">
						<div class="text-bold">Maps</div>
						<leaflet-map
							:is-view-only="true"
							v-bind:lat.sync="latitude"
							v-bind:lng.sync="longitude"
						/>
					</div>
				</div>
				<hr v-if="isShowBank" />
				<div class="row" v-if="isShowBank">
					<div class="col-12">
						<div class="text-bold">Bank Accounts</div>
					</div>
					<template v-if="userBankAccounts.length > 0">
						<div class="col-12 mt-2" v-for="(bank_account, idx) in userBankAccounts" :key="idx">
							<div class="d-flex align-items-center">
								<div class="integration-account__icon mr-3">
									<img class="img-responsive"
										:src="`https://cdn.ostock.id/img/banks/bank-${getBankImage(bank_account)}.png`"
										:alt="bank_account.label">
								</div>
								<div>
									<span class="text-bold">{{ bank_account.label }}</span> <br> {{ bank_account.account_name }} - {{ bank_account.account_number }}
								</div>
							</div>
						</div>
					</template>
					<template v-else>
						<div class="col-12">
							<div>Data Bank Tidak Ditemukan</div>
						</div>
					</template>
				</div>
			</div>
			<div v-else class="text-center">Data Supplier Tidak Ditemukan</div>
		</template>
		<template slot="footer">
			<button class="btn btn-light" @click="close">
				{{ $t('Close') }}
			</button>
			<button class="btn btn-primary" v-if="isShowIdentity" @click="openVerificationModal">
				{{ $t('Identity') }}
			</button>
		</template>
	</modal>
</template>

<script>
import homepageApi from '@/api/homepage';
import walletApi from '@/api/wallet';
import Modal from '@/components/Modal.vue';
import Overview from '@/components/Overview.vue';
import LeafletMap from '@/components//LeafletMap.vue';
import {
	getAxiosErrorMessage,
	validatePhoneNumber,
} from '@/lib/helper';
import { banks } from '@/lib/statics';

export default {
	name: 'ModalSupplierProfile',
	components: {
		Modal,
		Overview,
		LeafletMap,
	},
	props: {
		user: {
			type: Object,
			default: null,
		},
		isShowStats: {
			type: Boolean,
			default: true,
		},
		isShowBank: {
			type: Boolean,
			default: true,
		},
		isShowIdentity: {
			type: Boolean,
			default: true,
		},
		isShowLocation: {
			type: Boolean,
			default: true,
		},
		isShowPicture: {
			type: Boolean,
			default: true,
		},
		title: {
			type: String,
			default: 'Supplier Profile',
		},
	},
	data() {
		return {
			isLoading: false,
			stats: null,
			totalProducts: 0,
			balance: 0,
		};
	},
	methods: {
		close() {
			this.$emit('close');
		},
		openVerificationModal() {
			this.$emit('verification', this.user);
		},
		getSupplierDetail(supplierCode) {
			this.isLoading = true;
			const params = {
				supplier_code: supplierCode,
				all_products: true,
				include_stats: true,
			};

			const callback = (response) => {
				const supplierDetail = response.data;
				this.stats = supplierDetail?.statistics;
				this.totalProducts = supplierDetail?.total_products;
				this.isLoading = false;
			};

			const errorCallback = (e) => {
				this.isLoading = false;
				const message = getAxiosErrorMessage(e);
				console.log('message: ', message);
			};

			homepageApi.getSupplierDetail(params, callback, errorCallback);
		},
		getSupplierBalance(userId) {
			this.isLoading = true;

			const params = {
				user_id: userId,
			};

			const callback = (response) => {
				this.isLoading = false;
				const wallet = response.data;
				this.balance = wallet.balance;
			};
			const errorCallback = (e) => {
				this.isLoading = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Balance',
					text: message,
				});
			};
			walletApi.getSupplierBalance(params, callback, errorCallback);
		},
		getBankImage(bankAccount) {
			const bank = banks.find(curr => curr.name === bankAccount.name);
			return bank?.icon ? bankAccount.name : 'default';
		},
	},
	mounted() {
		if (this.user && this.user.supplier_code && this.isShowStats) {
			this.getSupplierBalance(this.user._id);
			this.getSupplierDetail(this.user.supplier_code);
		}
	},
	computed: {
		submissionCount() {
			return this.stats?.submission_count || 0;
		},
		userName() {
			const name = this.user?.name;
			return name || '-';
		},
		userEmail() {
			const email = this.user?.email;
			return email || '-';
		},
		userPhone() {
			let phone = '-';
			if (this.user && this.user.phone) {
				const validatedPhone = validatePhoneNumber(this.user.phone);
				phone = `+62${validatedPhone}`;
			}
			return phone;
		},
		userBusinessName() {
			const businessName = this.user?.store?.name;
			return businessName || '-';
		},
		userBusinessAddress() {
			const businessAddress = this.user?.store?.address;
			return businessAddress || '-';
		},
		userBusinessCity() {
			const businessAddress = this.user?.store?.address_detail?.city ?? '';
			const businessZip = this.user?.store?.address_detail?.zip ?? '';
			return `${businessAddress} - ${businessZip}`;
		},
		userBankAccounts() {
			const bankAccounts = this.user?.bank_accounts;
			return bankAccounts || [];
		},
		warehousePhoto() {
			const photo = this.user?.store?.warehouse_photo;
			return photo || '';
		},
		warehousePhotoWithOwner() {
			const photo = this.user?.store?.warehouse_photo_with_owner;
			return photo || '';
		},
		latitude() {
			const lat = this.user?.store?.location?.lat;
			return lat || '-';
		},
		longitude() {
			const lng = this.user?.store?.location?.lng;
			return lng || '-';
		},
		isMapEnabled() {
			const isEnabled = this.latitude !== '-' && this.longitude !== '-';
			return isEnabled;
		},
	},
};
</script>

<style scoped>
.store-logo-image {
	width: 100%;
	height: auto;
}

.store-logo-upload-button {
	height: 150px;
}

.camera-icon {
	font-size: 48px;
	width: 48px;
	height: 48px;
}
</style>
